import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X as XIcon, HelpCircle, Calendar, Clock, Users } from "lucide-react";
import axios from "axios";
import PageTransition, { Section } from "@/components/site/PageTransition";
import VinylLoader from "@/components/site/VinylLoader";
import { PulseLogo } from "@/components/site/Header";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtRange(startISO, endISO) {
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : null;
  const dateOpts = { weekday: "long", month: "long", day: "numeric", year: "numeric" };
  const timeOpts = { hour: "numeric", minute: "2-digit" };
  const dateStr = start.toLocaleDateString(undefined, dateOpts);
  const startTime = start.toLocaleTimeString(undefined, timeOpts);
  if (!end) return { dateStr, timeStr: startTime };
  const endTime = end.toLocaleTimeString(undefined, timeOpts);
  const sameDay = start.toDateString() === end.toDateString();
  return { dateStr, timeStr: sameDay ? `${startTime} – ${endTime}` : `${startTime} → ${end.toLocaleString(undefined, { ...dateOpts, ...timeOpts })}` };
}

export default function EventRsvp() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");

  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invite token. The link you followed is invalid."); return; }
    axios.get(`${API_BASE}/calendar/event/${id}`, { params: { t: token } })
      .then((r) => setEvent(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load event"));
  }, [id, token]);

  const respond = async (rsvp) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/calendar/event/${id}/rsvp`, { token, rsvp });
      // Refresh event to show updated state + counts
      const r = await axios.get(`${API_BASE}/calendar/event/${id}`, { params: { t: token } });
      setEvent(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to record RSVP");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <PageTransition>
        <Section className="pt-32 px-6 lg:px-10">
          <div className="max-w-xl mx-auto text-center" data-testid="event-error">
            <PulseLogo size={56} />
            <div className="mt-8 p-8 rounded-2xl bg-red-500/5 border border-red-500/20">
              <h2 className="font-display text-2xl font-bold mb-2">Invite link invalid</h2>
              <p className="text-zinc-400 text-sm">{error}</p>
              <Link to="/" className="glass-btn ghost mt-6 inline-flex">Back to home</Link>
            </div>
          </div>
        </Section>
      </PageTransition>
    );
  }

  if (!event) {
    return <div className="pt-40 flex justify-center"><VinylLoader size={80} label="Loading event"/></div>;
  }

  const { dateStr, timeStr } = fmtRange(event.start_date, event.end_date);
  const myRsvp = event.you?.rsvp;

  return (
    <PageTransition>
      {/* Hero */}
      <section className="relative pt-28 pb-12 px-6 lg:px-10" data-testid="event-page">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 50% 30%, ${event.color}25 0%, transparent 60%)`,
            }}
          />
        </div>

        <div className="relative max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <PulseLogo size={36} />
            <div>
              <div className="font-display font-bold tracking-tight text-base">PULSE MEDIA</div>
              <div className="label-mono" style={{ fontSize: 9 }}>YOU'RE INVITED</div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono mb-5 border"
              style={{ background: `${event.color}20`, color: event.color, borderColor: `${event.color}40` }}
            >
              <Calendar size={11} /> {dateStr}
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter leading-[0.95]" data-testid="event-title">
              {event.title}
            </h1>
            <div className="mt-4 inline-flex items-center gap-2 text-zinc-400">
              <Clock size={14}/> <span className="font-mono text-sm">{timeStr}</span>
            </div>
            {event.description && (
              <p className="mt-7 text-lg text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="event-description">
                {event.description}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* RSVP buttons */}
      <Section className="px-6 lg:px-10 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border border-white/10 bg-[#121215] p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="label-mono mb-1.5 text-zinc-500">/ Your RSVP</div>
                <div className="text-sm text-zinc-400">Replying as <span className="text-white">{event.you?.email}</span></div>
              </div>
              {event.you?.responded_at && (
                <div className="text-[11px] text-zinc-500 font-mono shrink-0">
                  Responded {new Date(event.you.responded_at).toLocaleString()}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <RsvpButton
                kind="yes"
                active={myRsvp === "yes"}
                disabled={submitting}
                onClick={() => respond("yes")}
                Icon={Check}
                label="Going"
                testid="rsvp-yes"
              />
              <RsvpButton
                kind="maybe"
                active={myRsvp === "maybe"}
                disabled={submitting}
                onClick={() => respond("maybe")}
                Icon={HelpCircle}
                label="Maybe"
                testid="rsvp-maybe"
              />
              <RsvpButton
                kind="no"
                active={myRsvp === "no"}
                disabled={submitting}
                onClick={() => respond("no")}
                Icon={XIcon}
                label="Can't make it"
                testid="rsvp-no"
              />
            </div>

            {/* Counts */}
            <div className="mt-7 pt-5 border-t border-white/5">
              <div className="label-mono text-zinc-500 inline-flex items-center gap-2 mb-3">
                <Users size={11}/> / Attendance
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <Stat n={event.rsvp_counts.yes} label="Going" color="text-emerald-400"/>
                <Stat n={event.rsvp_counts.maybe} label="Maybe" color="text-amber-400"/>
                <Stat n={event.rsvp_counts.no} label="Not going" color="text-red-400"/>
                <Stat n={event.rsvp_counts.pending} label="Pending" color="text-zinc-400"/>
              </div>
            </div>
          </div>

          <p className="text-center text-[11px] text-zinc-600 font-mono mt-6">
            POWERED BY PULSE MEDIA · YOUR LINK IS UNIQUE — DON'T SHARE IT
          </p>
        </div>
      </Section>
    </PageTransition>
  );
}

function RsvpButton({ kind, active, disabled, onClick, Icon, label, testid }) {
  const styles = {
    yes: active
      ? "border-emerald-400 bg-emerald-500/15 text-emerald-300"
      : "border-white/10 bg-[#0a0a0c] text-zinc-400 hover:border-emerald-400/40 hover:text-emerald-300",
    no: active
      ? "border-red-400 bg-red-500/15 text-red-300"
      : "border-white/10 bg-[#0a0a0c] text-zinc-400 hover:border-red-400/40 hover:text-red-300",
    maybe: active
      ? "border-amber-400 bg-amber-500/15 text-amber-300"
      : "border-white/10 bg-[#0a0a0c] text-zinc-400 hover:border-amber-400/40 hover:text-amber-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center gap-2 py-5 rounded-xl border-2 transition-all disabled:opacity-50 ${styles[kind]}`}
      data-testid={testid}
    >
      <Icon size={20}/>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function Stat({ n, label, color }) {
  return (
    <div>
      <div className={`font-display text-3xl font-bold tracking-tight ${color}`}>{n}</div>
      <div className="label-mono text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
